import { useNavigate } from "react-router-dom";

/** ------------------ CONTENT DATA (copy only) ------------------ */
const CONTENT = {
  jobsuite: {
    badge: "AI Resume Builder",
    heroTitle: "Build a job-winning resume as easily as sending a message",
    heroSub:
      "Turn your work experience, skills, and achievements into a clean, recruiter-friendly resume that’s optimized for Applicant Tracking Systems (ATS).",
    heroImg:
      "https://c.animaapp.com/mge1sasxzrNszH/img/image-60.png",
    sec1Title: "Build smarter, faster, and more effective resumes",
    sec1Points: [
      {
        title: "Smart Suggestions",
        desc: "Get tailored recommendations to highlight your top skills and achievements.",
      },
      {
        title: "Instant Formatting",
        desc: "Perfect alignment, consistent spacing, and elegant layout — automatically handled.",
      },
      {
        title: "ATS Optimization",
        desc: "Ensure your resume passes ATS filters with keyword alignment and readability checks.",
      },
      {
        title: "Export in Clicks",
        desc: "Instantly download PDF or DOCX versions optimized for recruiters.",
      },
      {
        title: "Multiple Styles & Templates",
        desc: "Choose designs ranging from modern to minimal based on your role.",
      },
      {
        title: "One Profile, Many Resumes",
        desc: "Generate custom resumes for different job applications without retyping.",
      },
    ],
    sec2Title: "Choose from templates that match your style",
    sec2Sub:
      "From modern and creative to professional and minimal — pick a design that fits your career goals.",
    sec2Button: "Explore all Templates →",
  },

  studysuite: {
    badge: "AI Document Builder",
    heroTitle: "Create standout SOPs, LORs, and resumes with AI precision",
    heroSub:
      "Build professional documents for your university applications — personalized, plagiarism-safe, and formatted to impress admissions committees.",
    heroImg:
      "https://c.animaapp.com/mge1sasxzrNszH/img/image-60.png", // Replace with a doc builder visual if available
    sec1Title: "Draft smarter, faster, and more personalized documents",
    sec1Points: [
      {
        title: "AI SOP Generator",
        desc: "Turn your academic background and goals into a clear, compelling SOP in minutes.",
      },
      {
        title: "LOR Assistant",
        desc: "Generate recommendation letters customized to professor or employer tone.",
      },
      {
        title: "CV & Resume Optimizer",
        desc: "Highlight academic projects, internships, and research effectively.",
      },
      {
        title: "Originality & Tone Check",
        desc: "Ensure originality and maintain professional, consistent tone across all documents.",
      },
      {
        title: "Template & Format Library",
        desc: "University-compliant SOP, LOR, and CV templates with clear structure.",
      },
      {
        title: "Application Document Sync",
        desc: "Keep all your documents aligned with your course and country requirements.",
      },
    ],
    sec2Title: "Pick from proven templates trusted by top admits",
    sec2Sub:
      "From formal academic styles to creative statement formats — find a layout that fits your story and university requirements.",
    sec2Button: "View All Templates →",
  },
};

/**
 * Desktop4 Component
 * @param {"jobsuite"|"studysuite"} suite - content variant (defaults to "jobsuite")
 */
const Desktop4 = ({ suite = "jobsuite" }) => {
  const navigate = useNavigate();
  const S = CONTENT[suite] || CONTENT.jobsuite;

  return (
    <div className="bg-[#f9f9f9] w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#06010A] to-[#260345] text-white flex flex-col md:flex-row items-center justify-between px-8 md:px-16 lg:px-24 py-20 overflow-hidden">
        {/* Decorative Ellipses */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-[400px] h-[400px] bg-[#7B3FE4]/40 rounded-full blur-3xl top-[-100px] left-[-100px]" />
          <div className="absolute w-[350px] h-[350px] bg-[#E6C1FF]/30 rounded-full blur-3xl bottom-[-150px] right-[-100px]" />
        </div>

        {/* Left Content */}
        <div className="relative z-10 max-w-xl">
          <h3 className="text-lg text-[#ffffffbf] mb-3">{S.badge}</h3>
          <h1 className="text-4xl md:text-5xl font-semibold leading-tight mb-6">
            {S.heroTitle}
          </h1>
          <p className="text-gray-200 mb-8 text-base md:text-lg">{S.heroSub}</p>

          <button
            onClick={() => navigate("/auth")}
            className="bg-[#ffc403] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#e6b800] transition"
          >
            Get Started
          </button>
        </div>

        {/* Right Image */}
        <div className="relative z-10 mt-12 md:mt-0">
          <img
            src={S.heroImg}
            alt="Preview"
            className="w-[350px] md:w-[450px] object-contain"
          />
        </div>
      </section>

      {/* Feature Section */}
      <section className="bg-[#f9f9f9] py-20 px-6 md:px-16 lg:px-24 text-[#22033c]">
        <h2 className="text-3xl md:text-4xl font-semibold text-center mb-4">
          {S.sec1Title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12 mt-12 max-w-6xl mx-auto">
          {S.sec1Points.map((point, index) => (
            <div
              key={index}
              className="flex flex-col items-center text-center space-y-3"
            >
              <h3 className="font-semibold text-lg md:text-xl">
                {point.title}
              </h3>
              <p className="text-base text-[#22033c]/80">{point.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Template Showcase Section */}
      <section className="bg-[#f9f9f9] py-20 px-6 md:px-16 lg:px-24 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold text-[#22033c] mb-4">
          {S.sec2Title}
        </h2>
        <p className="text-[#22033c]/80 text-lg mb-10 max-w-2xl mx-auto">
          {S.sec2Sub}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {[1, 2, 3, 4].map((_, i) => (
            <div
              key={i}
              className="bg-[#d9d9d9] h-[300px] rounded-lg shadow-md hover:scale-105 transition"
            />
          ))}
        </div>

        <button className="mt-10 text-[#22033c] font-semibold text-lg hover:underline">
          {S.sec2Button}
        </button>
      </section>
    </div>
  );
};

export default Desktop4;
